import React, { FormEvent, useCallback, useEffect, useState } from "react";
import { ArrowLeft, Save } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { GetDocumentById, UpdateDocumentApi } from "../apis/documents";
import { DocumentRecord } from "./documentTypes";
import RichTextEditor from "./RichTextEditor";
import { useContentSearch } from "./useContentSearch";
import SearchBar from "./SearchBar";

export default function EditDocument() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm]     = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const search = useContentSearch();

  // Capture the contentEditable div inside RichTextEditor
  const captureEditorRef = useCallback((wrapperEl: HTMLDivElement | null) => {
    if (!wrapperEl) return;
    const ce = wrapperEl.querySelector("[contenteditable='true']") as HTMLDivElement | null;
    if (ce) search.contentRef.current = ce;
  }, [search.contentRef]);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await GetDocumentById(id);
        const doc: DocumentRecord = response?.data;
        setForm({
          title:        doc.title        || "",
          subtitle:     doc.subtitle     || "",
          description:  doc.description  || "",
          content_text: doc.content_text || doc.extracted_text || "",
          category:     doc.category     || "",
          tags:         (doc.tags        || []).join(", "),
          keywords:     (doc.keywords    || []).join(", "),
          headings:     (doc.headings    || []).join(", "),
          subheadings:  (doc.subheadings || []).join(", "),
          is_pinned:    !!doc.is_pinned,
          is_archived:  !!doc.is_archived,
          is_active:    doc.is_active !== false,
        });
      } catch (error) {
        console.error(error);
        toast.error("Failed to load document");
      }
    };
    load();
  }, [id]);

  const update = (key: string, value: any) =>
    setForm((prev: any) => ({ ...prev, [key]: value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await UpdateDocumentApi(id, form);
      toast.success("Document updated");
      navigate(`/documents/${id}`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to update document");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="mt-8 h-80 animate-pulse rounded-lg border border-slate-800 bg-slate-900/70" />;

  return (
    <div className="mt-8 max-w-5xl pb-10">
      <button onClick={() => navigate(`/documents/${id}`)}
        className="mb-4 inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800">
        <ArrowLeft size={16} /> Back
      </button>

      <form onSubmit={submit} className="rounded-lg border border-slate-800 bg-slate-900/70 p-5">
        {/* Header */}
        <div className="mb-5 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-500/10 text-blue-300"><Save /></div>
          <div>
            <h1 className="text-2xl font-black text-white">Edit Document</h1>
            <p className="text-sm text-slate-400">Update metadata, searchable content and visibility.</p>
          </div>
        </div>

        {/* Meta fields */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white" placeholder="Title *"                       value={form.title}       onChange={(e) => update("title",       e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white" placeholder="Subtitle"                       value={form.subtitle}    onChange={(e) => update("subtitle",    e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white" placeholder="Category"                       value={form.category}    onChange={(e) => update("category",    e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white" placeholder="Tags, comma separated"          value={form.tags}        onChange={(e) => update("tags",        e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white" placeholder="Keywords, comma separated"      value={form.keywords}    onChange={(e) => update("keywords",    e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white" placeholder="Headings, comma separated"      value={form.headings}    onChange={(e) => update("headings",    e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white md:col-span-2" placeholder="Subheadings, comma separated" value={form.subheadings} onChange={(e) => update("subheadings", e.target.value)} />
          <textarea className="min-h-28 rounded-lg border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white md:col-span-2" placeholder="Description" value={form.description} onChange={(e) => update("description", e.target.value)} />
        </div>

        {/* Content editor + search bar */}
        <div className="mt-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
            <label className="text-sm font-semibold text-slate-200">Content editor</label>
            <div className="flex flex-wrap items-center gap-2">
              <SearchBar
                searchQuery={search.searchQuery}
                setSearchQuery={search.setSearchQuery}
                matchCount={search.matchCount}
                currentMatch={search.currentMatch}
                goNext={search.goNext}
                goPrev={search.goPrev}
                clearSearch={search.clearSearch}
              />
              <span className="text-xs text-slate-500">Use headings, lists, links and formatted text</span>
            </div>
          </div>

          {/* Wrap to capture contentEditable ref */}
          <div ref={captureEditorRef}>
            <RichTextEditor value={form.content_text} onChange={(value) => update("content_text", value)} />
          </div>
        </div>

        {/* Flags */}
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.is_pinned}   onChange={(e) => update("is_pinned",   e.target.checked)} /> Pinned</label>
          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.is_archived} onChange={(e) => update("is_archived", e.target.checked)} /> Archived</label>
          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.is_active}   onChange={(e) => update("is_active",   e.target.checked)} /> Active</label>
        </div>

        <div className="mt-6 flex justify-end">
          <button disabled={saving} className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-500 disabled:opacity-60">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
